import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import UpgradeButton from "./UpgradeButton";

const plans = [
  {
    name: "Try it. Feel the difference.",
    description: "Everything you need to build awareness and momentum.",
    price: "Free",
    features: [
      { name: "Up to 3 core habits", included: true },
      { name: "Essential habit tracking", included: true },
      { name: "Daily mood tracking", included: true },
      { name: "Beautiful dark/light themes", included: true },
      { name: "Export your progress data", included: true },
      { name: "Custom habit colors", included: false },
      { name: "Import data from other apps", included: false },
      { name: "Advanced progress insights", included: false },
      { name: "Priority support", included: false },
      { name: "Early access to new features", included: false },
    ],
    cta: "Start Without Commitment",
    ctaLink: "/auth",
    popular: false
  },
  {
    name: "Master",
    description: "Full control for those who take consistency seriously. One-time payment.",
    price: "$19",
    priceDetail: "USD one-time payment",
    features: [
      { name: "Unlimited habits for total life control", included: true },
      { name: "Advanced analytics & insights", included: true },
      { name: "Daily mood tracking", included: true },
      { name: "Beautiful dark/light themes", included: true },
      { name: "Export your progress data", included: true },
      { name: "Custom colors to stack your habits", included: true },
      { name: "Import data from other apps", included: true },
      { name: "Detailed progress reports (coming soon)", included: true },
      { name: "Priority support when you need help", included: true },
      { name: "First access to powerful new features", included: true },
    ],
    cta: "Commit to Consistency",
    ctaLink: "/pricing",
    popular: true
  }
];

export default function PricingPlans() {
  const { user, isPremium } = useAuth();

  return (
    <div className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary">Pricing</h2>
          <p className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            Upgrade your clarity. 
          </p>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            Start with our free tools and see your patterns unfold. Upgrade when you're ready to go deeper for life, no subscriptions.
          </p>
        </div>

        <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-2">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={cn(
                "rounded-3xl p-8 ring-1 ring-muted xl:p-10",
                plan.popular && "ring-2 ring-primary bg-muted/50"
              )}
            >
              <div className="flex items-center justify-between gap-x-4">
                <h3 className="text-lg font-semibold leading-8">{plan.name}</h3>
                {plan.popular && (
                  <p className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold leading-5 text-primary">
                    Complete Control
                  </p>
                )}
              </div>
              <p className="mt-4 text-sm leading-6 text-muted-foreground">
                {plan.description}
              </p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-bold">{plan.price}</span>
                {plan.priceDetail && (
                  <span className="text-sm text-muted-foreground">
                    {plan.priceDetail}
                  </span>
                )}
              </p>
              {plan.popular ? (
                isPremium ? (
                  <Button
                    className="mt-6 w-full"
                    variant="default"
                    disabled
                  >
                    Your Current Plan
                  </Button>
                ) : (
                  <UpgradeButton
                    className="mt-6 w-full"
                    variant="default"
                  />
                )
              ) : (
                <Button
                  className="mt-6 w-full"
                  variant="outline"
                  asChild
                >
                  <Link to={plan.ctaLink}>{plan.cta}</Link>
                </Button>
              )}
              <ul role="list" className="mt-8 space-y-3 text-sm leading-6">
                {plan.features.map((feature) => (
                  <li key={feature.name} className="flex gap-x-3">
                    {feature.included ? (
                      <Check className="h-6 w-5 flex-none text-primary" />
                    ) : (
                      <X className="h-6 w-5 flex-none text-muted-foreground" />
                    )}
                    <span className={cn(!feature.included && "text-muted-foreground")}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mx-auto max-w-[700px] text-center text-muted-foreground text-[10px] md:text-xs mt-4">
          If you can not afford the app but still would like premium access for your own journey, send us an email <a href="mailto:abdullah@mon10.org" className="underline">here</a> letting us know your situation and we'll gift you a membership
        </p>
      </div>
    </div>
  );
} 